import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Chip,
  IconButton,
  Alert,
  useTheme
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CodeIcon from '@mui/icons-material/Code';

export interface ErrorListProps {
  /**
   * Liste des erreurs
   */
  errors: Array<{
    line: number;
    message: string;
    data?: any;
  }>;
  
  /**
   * Titre de la liste
   * @default "Erreurs d'importation"
   */
  title?: string;
  
  /**
   * Nombre maximal d'erreurs à afficher initialement
   * @default 5
   */
  initialLimit?: number;
}

/**
 * Composant pour afficher la liste des erreurs d'importation
 */
const ErrorList = ({
  errors,
  title = "Erreurs d'importation",
  initialLimit = 5
}: ErrorListProps): React.ReactElement => {
  const theme = useTheme();
  const [expandedItems, setExpandedItems] = useState<number[]>([]);
  const [showAll, setShowAll] = useState<boolean>(false);
  
  // Limiter le nombre d'erreurs affichées
  const displayedErrors = showAll ? errors : errors.slice(0, initialLimit);
  
  // Gérer l'expansion d'un élément
  const handleToggleExpand = (index: number) => {
    setExpandedItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index) 
        : [...prev, index]
    );
  };
  
  // Gérer l'affichage de toutes les erreurs
  const handleToggleShowAll = () => {
    setShowAll(prev => !prev);
  };
  
  // Formater les données pour l'affichage
  const formatData = (data: any): string => {
    try {
      return JSON.stringify(data, null, 2);
    } catch (e) {
      return String(data);
    }
  };
  
  return (
    <Box sx={{ width: '100%', mb: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {title}
          </Typography>
          <Chip 
            label={`${errors.length} erreur${errors.length > 1 ? 's' : ''}`} 
            color="error" 
            variant="outlined" 
          />
        </Box>
        
        {errors.length === 0 ? (
          <Alert severity="success">
            Aucune erreur n'a été détectée lors de l'importation.
          </Alert>
        ) : (
          <>
            <List sx={{ width: '100%' }}>
              {displayedErrors.map((error, index) => (
                <React.Fragment key={`error-${index}`}>
                  <ListItem 
                    alignItems="flex-start"
                    secondaryAction={
                      error.data && (
                        <IconButton 
                          edge="end" 
                          onClick={() => handleToggleExpand(index)}
                          aria-label="expand"
                        >
                          {expandedItems.includes(index) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      )
                    }
                    sx={{ 
                      backgroundColor: theme.palette.error.light + '10', // 10% d'opacité
                      borderRadius: 1,
                      mb: 1
                    }}
                  >
                    <ListItemIcon>
                      <ErrorIcon color="error" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="subtitle1" component="span">
                            Ligne {error.line}
                          </Typography>
                          <Chip 
                            label={`Ligne ${error.line}`} 
                            size="small" 
                            color="default" 
                            sx={{ ml: 1, height: 20 }} 
                          />
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="body2"
                          color="text.primary"
                          sx={{ mt: 1 }}
                        >
                          {error.message}
                        </Typography>
                      }
                    />
                  </ListItem>
                  
                  {error.data && (
                    <Collapse in={expandedItems.includes(index)} timeout="auto" unmountOnExit>
                      <Box sx={{ pl: 9, pr: 2, pb: 2 }}>
                        <Paper 
                          variant="outlined" 
                          sx={{ 
                            p: 2, 
                            backgroundColor: theme.palette.grey[100],
                            maxHeight: 200,
                            overflow: 'auto'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <CodeIcon fontSize="small" sx={{ mr: 1 }} />
                            <Typography variant="caption" color="text.secondary">
                              Données de la ligne
                            </Typography>
                          </Box>
                          <pre style={{ margin: 0, fontSize: '0.75rem' }}>
                            {formatData(error.data)}
                          </pre>
                        </Paper>
                      </Box>
                    </Collapse>
                  )}
                  
                  {index < displayedErrors.length - 1 && (
                    <Divider variant="inset" component="li" />
                  )}
                </React.Fragment>
              ))}
            </List>
            
            {errors.length > initialLimit && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Chip
                  label={showAll ? "Afficher moins" : `Afficher toutes les erreurs (${errors.length})`}
                  onClick={handleToggleShowAll}
                  color="primary"
                  variant="outlined"
                  clickable
                />
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ErrorList;